/**
 * Utility for resolving fuel type across different template channels and contexts.
 * Handles the complex logic of determining fuel type from various sources:
 * - propFuelType (explicitly passed)
 * - Template key suffix (Web uses __, Email/HER/SMS use .)
 * - Project attributes (fallback)
 */

import { getFuelTypeFromTemplateKey } from './templates-from-feature-metrics';

export interface ResolveFuelTypeParams {
  /** Product type from template key (e.g., 'WEB', 'EMAIL', 'SMS', 'HER') */
  productType: string;
  /** Full template key (e.g., 'DASHBOARD.HOME.BILL_HISTORY__GAS' or 'EMAIL.DF.USER_WELCOME') */
  templateKey: string;
  /** Fuel type explicitly passed as prop (can be undefined, empty string, or valid fuel type) */
  propFuelType: string | undefined;
  /** Project attributes containing fallback fuel_type */
  projectAttributes: any;
  /** Optional channel override (e.g., 'Web', 'Email', 'SMS', 'HER') */
  channel?: string;
}

/**
 * Get project default fuel type from project attributes.
 * Handles multiple formats: string, array, or comma-separated.
 */
function getProjectDefaultFuelType(projectAttributes: any): string {
  if (!projectAttributes?.fuel_type) {
    return 'ELECTRIC';
  }

  // Handle string format (may be comma-separated)
  if (typeof projectAttributes.fuel_type === 'string') {
    const fuelTypes = projectAttributes.fuel_type.split(',').map((f: string) => f.trim());
    return fuelTypes[0]?.toUpperCase() || 'ELECTRIC';
  }

  // Handle array format
  if (Array.isArray(projectAttributes.fuel_type)) {
    const fuelTypeArray = projectAttributes.fuel_type as any[];
    return fuelTypeArray.length > 0 
      ? String(fuelTypeArray[0]).toUpperCase() 
      : 'ELECTRIC';
  }

  return 'ELECTRIC';
}

/**
 * Resolve fuel type for a template using a consistent priority chain.
 * 
 * Priority:
 * 1. For Web templates: propFuelType > template key extraction > project default
 * 2. For Email/SMS templates: propFuelType (including '') > template key extraction > '' (no fuel)
 * 3. For HER templates: propFuelType > template key extraction > project default
 * 
 * @returns Fuel type string (can be empty string '' for Email/SMS templates without fuel type)
 * 
 * @example
 * // Web template with fuel type in prop
 * resolveFuelType({
 *   productType: 'WEB',
 *   templateKey: 'DASHBOARD.HOME.BILL_HISTORY',
 *   propFuelType: 'GAS',
 *   projectAttributes: {}
 * }) // → 'GAS'
 * 
 * @example
 * // Email template without fuel type
 * resolveFuelType({
 *   productType: 'EMAIL',
 *   templateKey: 'EMAIL.DF.USER_WELCOME',
 *   propFuelType: '',
 *   projectAttributes: {}
 * }) // → ''
 * 
 * @example
 * // Web template extracting fuel from key
 * resolveFuelType({
 *   productType: 'WEB',
 *   templateKey: 'DASHBOARD.HOME.BILL_HISTORY__GAS',
 *   propFuelType: undefined,
 *   projectAttributes: {}
 * }) // → 'GAS'
 */
export function resolveFuelType(params: ResolveFuelTypeParams): string {
  const { productType, templateKey, propFuelType, projectAttributes, channel } = params;

  // Determine if this is a Web template (by productType or channel)
  const isWebTemplate = productType === 'WEB' || channel === 'Web';

  // WEB TEMPLATES: Always have a fuel type, never empty string
  if (isWebTemplate) {
    // 1. Use propFuelType if provided and not empty
    if (propFuelType && propFuelType !== '') {
      return propFuelType;
    }

    // 2. Extract from template key (e.g., "DASHBOARD.HOME.BILL_HISTORY__GAS" → "GAS")
    const extractedFuelType = getFuelTypeFromTemplateKey(templateKey);
    if (extractedFuelType) {
      return extractedFuelType;
    }

    // 3. Fall back to project default fuel type
    return getProjectDefaultFuelType(projectAttributes);
  }

  // EMAIL/SMS/HER TEMPLATES: May have no fuel type (empty string)
  
  // 1. If propFuelType is explicitly provided (even empty string), use it
  //    Empty string '' means the template has no fuel type (e.g., USER_WELCOME email)
  if (propFuelType !== undefined) {
    return propFuelType;
  }

  // 2. Extract from template key suffix
  const templateFuelType = getFuelTypeFromTemplateKey(templateKey);
  if (templateFuelType) {
    return templateFuelType;
  }

  // 3. Email/SMS templates without fuel suffix genuinely have no fuel type
  if (productType === 'EMAIL' || productType === 'SMS') {
    return '';
  }

  // 4. For HER or other templates, fall back to project attributes
  return getProjectDefaultFuelType(projectAttributes);
}
